import { useQuery } from '@tanstack/react-query';
import type { Account, Quota } from '@/api';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { STALE_TIMES } from '@/api/queryClient';

/**
 * One mailbox's quota, for the shell's status bar and drawer.
 *
 * The shell used to read `account.quota`, which the Go backend never puts on
 * the account list -- quota is its own endpoint, relayed from Poste or the
 * mailbox's IMAP QUOTA -- so the line was drawn only against the mocks. This
 * is the same query, under the same key, that Settings → Accounts runs, so the
 * two share one cache entry and one request.
 *
 * Not fetched at all when the account says its host cannot know (a false
 * `capabilities.quota`): the server would only answer 501.
 */
export function useAccountQuota(account: Account | undefined): Quota | undefined {
  const api = useApi();
  const enabled = account !== undefined && account.capabilities.quota;
  const { data } = useQuery({
    queryKey: queryKeys.account(account?.id ?? '').quota(),
    queryFn: ({ signal }) => api.accounts.quota(account?.id ?? '', signal),
    staleTime: STALE_TIMES.metadata,
    enabled,
  });
  return enabled ? data : undefined;
}

/** What the status bar and the drawer say. */
export interface QuotaSummary {
  percent: number;
  /** `percent` as words: "37%", or "<1%" for mail that rounds to nothing. */
  percentText: string;
  /** The allowance in bytes. */
  limitBytes: number;
}

/** Whether anything is stored at all, as far as the figures can tell. */
export function quotaHoldsMail(quota: Quota): boolean {
  return quota.storageUsage > 0 || quota.countUsage > 0;
}

/**
 * The share of the allowance used, as every quota surface prints it.
 *
 * One formatter, because there were two: the status bar and the drawer said
 * "0%" for the same mailbox Settings → Accounts called "(<1%)". Under half a
 * percent of the allowance rounds to 0, and "0%" beside mail that exists reads
 * as empty, so a mailbox that holds anything is "<1%" until it reaches 1.
 */
export function formatQuotaPercent(percent: number, holdsMail: boolean): string {
  return percent <= 0 && holdsMail ? '<1%' : `${Math.max(0, percent)}%`;
}

/**
 * Poste writes storage in MEBIBYTES (Settings → Accounts measured it), and 0
 * for "no limit configured" -- which gets no percentage at all rather than a
 * percentage of nothing.
 */
export function summarizeQuota(quota: Quota | undefined): QuotaSummary | undefined {
  if (!quota || !(quota.storageLimit > 0)) return undefined;
  const percent = Math.round((quota.storageUsage / quota.storageLimit) * 100);
  return {
    percent,
    percentText: formatQuotaPercent(percent, quotaHoldsMail(quota)),
    limitBytes: quota.storageLimit * 1024 * 1024,
  };
}
